import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import ParkingIcon from 'mdi-react/ParkingIcon';
import AlertIcon from 'mdi-react/AlertIcon';
import * as parkingStyles from '../office-parking.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';

import type Parking from '../../parkings/models/parking';
import { updateParking } from '../../parkings/services/parkings.service';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import isThresholdPairValid from '../../../core/utils/threshold-utils';

interface Props {
  readonly initialParking: Parking;
  readonly onUpdated?: (parking: Parking) => void;
}

const clampPercentage = (value: number | null | undefined): number => Math.min(100, Math.max(0, value ?? 0));

const OfficeParkingCard = ({ initialParking, onUpdated }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const keycloak = useKeycloak();
  const [parking, setParking] = useState<Parking>(initialParking);
  const [isSaving, setIsSaving] = useState(false);

  const isValid = isThresholdPairValid(parking.thresholdMinPercentage, parking.thresholdMaxPercentage);

  const isChanged =
    parking.thresholdMinPercentage !== initialParking.thresholdMinPercentage ||
    parking.thresholdMaxPercentage !== initialParking.thresholdMaxPercentage;

  const handleSave = async (): Promise<void> => {
    if (!isValid) {
      return;
    }

    setIsSaving(true);
    const result = await updateParking(initialParking, parking, keycloak);
    setIsSaving(false);

    if (result) {
      notify(t('parking-saved-success-message'), true);
      onUpdated?.(result);
    } else {
      notify(t('parking-saved-error-message'), false);
    }
  };

  const lowPercentage = clampPercentage(parking.thresholdMinPercentage);
  const highPercentage = clampPercentage(parking.thresholdMaxPercentage);

  // Thresholds are percentages, so spell out what they mean in actual spaces — 80% of 2 is 2.
  const spacesAt = (percentage: number): number => Math.round((parking.capacity * percentage) / 100);

  const capacityLabel = `${parking.capacity} ${parking.capacity === 1 ? t('parking-space') : t('parking-spaces')}`;

  const hasThresholds = parking.thresholdMinPercentage != null && parking.thresholdMaxPercentage != null;

  let legendText = t('parking-threshold-not-set') ?? '';
  if (hasThresholds) {
    legendText = isValid
      ? t('parking-threshold-legend')
        .replace('[x]', spacesAt(lowPercentage).toString())
        .replace('[y]', spacesAt(highPercentage).toString())
        .replace('[z]', parking.capacity.toString())
      : t('dashboard-thresholds-validation-message') ?? '';
  }

  // The id is built from a fixed key, not from the translated label: label text contains spaces
  // (invalid in an id) and changes with the UI language.
  const renderThresholdField = (
    fieldKey: 'min' | 'max',
    label: string,
    tooltip: string,
    value: number | null | undefined,
    onChange: (next: number | undefined) => void,
  ): React.JSX.Element => {
    const inputId = `parking-${parking.id ?? parking.name}-${fieldKey}`;

    return (
      <div className={style(parkingStyles.field)}>
        <label className={style(parkingStyles.fieldLabel)} htmlFor={inputId}>
          {label}
        </label>
        <input
          id={inputId}
          type="number"
          min={0}
          max={100}
          title={tooltip}
          aria-invalid={!isValid}
          className={style(parkingStyles.fieldInput(!isValid))}
          value={value?.toString() ?? ''}
          onChange={(event): void => {
            onChange(event.target.value === '' ? undefined : Number(event.target.value));
          }}
        />
        <span className={style(parkingStyles.fieldUnit)}>%</span>
      </div>
    );
  };

  return (
    <div className={style(parkingStyles.row)}>
      <div className={style(parkingStyles.rowTop)}>
        <div className={style(parkingStyles.tile)}>
          <ParkingIcon size={20} />
        </div>
        <div className={style(parkingStyles.rowWho)}>
          <span className={style(parkingStyles.rowName)}>{parking.name}</span>
          <span className={style(parkingStyles.rowCapacity)}>{capacityLabel}</span>
        </div>
        {!isValid && (
          <span className={style(parkingStyles.warningIcon)} title={t('dashboard-thresholds-validation-message').toString()}>
            <AlertIcon size={20} />
          </span>
        )}
      </div>

      <div className={style(parkingStyles.fields(isMobile))}>
        {renderThresholdField(
          'min',
          t('threshold-min') ?? '',
          t('threshold-min-info-message') ?? '',
          parking.thresholdMinPercentage,
          (next): void => setParking({ ...parking, thresholdMinPercentage: next }),
        )}
        {renderThresholdField(
          'max',
          t('threshold-max') ?? '',
          t('threshold-max-info-message') ?? '',
          parking.thresholdMaxPercentage,
          (next): void => setParking({ ...parking, thresholdMaxPercentage: next }),
        )}
        <div>
          <div className={style(parkingStyles.band(isValid && hasThresholds))}>
            {isValid && hasThresholds && (
              <>
                <span className={style(parkingStyles.bandNormal(lowPercentage, Math.max(0, highPercentage - lowPercentage)))} />
                <span className={style(parkingStyles.bandBusy(highPercentage))} />
              </>
            )}
          </div>
          <span className={style(parkingStyles.legend(!isValid))}>{legendText}</span>
        </div>
      </div>

      {isChanged && (
        <div className={style(parkingStyles.saveRow)}>
          <div className={style(surfaceStyles.inlineSaveWrap)}>
            <PrimaryButton fullWidth onClick={handleSave} loading={isSaving} disabled={!isValid}>
              {t('save')}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficeParkingCard;
